import DateAndValue from "../DateAndValue";
import { DESCENDANT_ORDER, MONTHLY } from "../../config";

export default class DupontRatios {
  constructor({
    ascendantDates = [],
    datesFormatted = [],
    yearsFormatted = [],
    totalAssets = [],
    totalEquity = [],
    totalSales = [],
    interestExpense = [],
    EBT = [],
    netIncome = [],
    yearlyTotalAssets = [],
    yearlyTotalEquity = [],
    yearlyTotalSales = [],
    yearlyInterestExpense = [],
    yearlyEBT = [],
    yearlyNetIncome = [],
    dateOrder = DESCENDANT_ORDER,
    prefix = MONTHLY,
  }) {
    this.ascendantDates = ascendantDates;
    this.datesFormatted = datesFormatted;
    this.yearsFormatted = yearsFormatted;
    this.totalAssets = totalAssets;
    this.totalEquity = totalEquity;
    this.totalSales = totalSales;
    this.interestExpense = interestExpense;
    this.EBT = EBT;
    this.netIncome = netIncome;
    this.yearlyTotalAssets = yearlyTotalAssets;
    this.yearlyTotalEquity = yearlyTotalEquity;
    this.yearlyTotalSales = yearlyTotalSales;
    this.yearlyInterestExpense = yearlyInterestExpense;
    this.yearlyEBT = yearlyEBT;
    this.yearlyNetIncome = yearlyNetIncome;
    this.dateOrder = dateOrder;
    this.prefix = prefix;
  }

  get EBIT() {
    const { EBT, interestExpense } = this;
    return this.getArraySum(EBT, interestExpense);
  }

  get yearlyEBIT() {
    const { yearlyEBT, yearlyInterestExpense } = this;
    return this.getArraySum(yearlyEBT, yearlyInterestExpense, { yearly: true });
  }

  get averageEquity() {
    const { totalEquity } = this;
    return this.getValueLastTwelveMonth(totalEquity, { average: true });
  }

  get yearlyAverageEquity() {
    const { averageEquity } = this;
    return this.getElementByYear(averageEquity);
  }

  get averageTotalAssets() {
    const { totalAssets } = this;
    return this.getValueLastTwelveMonth(totalAssets, { average: true });
  }

  get yearlyAverageTotalAssets() {
    const { averageTotalAssets } = this;
    return this.getElementByYear(averageTotalAssets);
  }

  get monthlyOperatingProfitMargin() {
    const { EBIT, totalSales } = this;
    return this.getValueLastTwelveMonth(EBIT, { array2: totalSales });
  }

  get yearlyOperatingProfitMargin() {
    const { yearlyEBIT, yearlyTotalSales } = this;
    return this.getArrayMultiply(yearlyEBIT, yearlyTotalSales, { divide: true, yearly: true });
  }

  get monthlyAssetTurnover() {
    const { totalSales, averageTotalAssets } = this;
    return this.getValueLastTwelveMonth(totalSales, { array2: averageTotalAssets, oneDivider: true });
  }

  get yearlyAssetTurnover() {
    const { yearlyTotalSales, yearlyAverageTotalAssets } = this;
    return this.getArrayMultiply(yearlyTotalSales, yearlyAverageTotalAssets, { divide: true, yearly: true });
  }

  get monthlyReturnonAsset() {
    const { monthlyOperatingProfitMargin, monthlyAssetTurnover } = this;
    return this.getArrayMultiply(monthlyOperatingProfitMargin, monthlyAssetTurnover);
  }

  get yearlyReturnonAsset() {
    const { yearlyOperatingProfitMargin, yearlyAssetTurnover } = this;
    return this.getArrayMultiply(yearlyOperatingProfitMargin, yearlyAssetTurnover, { yearly: true });
  }

  get monthlyTaxBurden() {
    const { netIncome, EBT } = this;
    return this.getValueLastTwelveMonth(netIncome, { array2: EBT });
  }

  get yearlyTaxBurden() {
    const { yearlyNetIncome, yearlyEBT } = this;
    return this.getArrayMultiply(yearlyNetIncome, yearlyEBT, { divide: true, yearly: true });
  }

  get monthlyInterestBurden() {
    const { EBT, EBIT } = this;
    return this.getValueLastTwelveMonth(EBT, { array2: EBIT });
  }

  get yearlyInterestBurden() {
    const { yearlyEBT, yearlyEBIT } = this;
    return this.getArrayMultiply(yearlyEBT, yearlyEBIT, { divide: true, yearly: true });
  }

  get monthlyFinancialLeverage() {
    const { averageTotalAssets, averageEquity } = this;
    return this.getArrayMultiply(averageTotalAssets, averageEquity, { divide: true });
  }

  get yearlyFinancialLeverage() {
    const { yearlyAverageTotalAssets, yearlyAverageEquity } = this;
    return this.getArrayMultiply(yearlyAverageTotalAssets, yearlyAverageEquity, { divide: true, yearly: true });
  }

  get monthlyReturnonEquity() {
    const {
      monthlyTaxBurden,
      monthlyInterestBurden,
      monthlyReturnonAsset,
      monthlyFinancialLeverage,
    } = this;
    return this.getArrayMultiply(
      this.getArrayMultiply(monthlyTaxBurden, monthlyInterestBurden),
      this.getArrayMultiply(monthlyReturnonAsset, monthlyFinancialLeverage)
    );
  }

  get yearlyReturnonEquity() {
    const {
      yearlyTaxBurden,
      yearlyInterestBurden,
      yearlyReturnonAsset,
      yearlyFinancialLeverage,
    } = this;
    return this.getArrayMultiply(
      this.getArrayMultiply(yearlyTaxBurden, yearlyInterestBurden, { yearly: true }),
      this.getArrayMultiply(yearlyReturnonAsset, yearlyFinancialLeverage, { yearly: true }),
      { yearly: true }
    );
  }

  //get average of last 12 months
  getValueLastTwelveMonth(array1, options = { array2: [], average: false, oneDivider: false }) {
    const TWELVE_MONTHS = 12;

    return this.ascendantDates.map((date, index) => {
      //LTM comes form earlier 12 months' value regardless dateorder
      let value = 0;
      let numerator = 0;
      let divider = 0;
      let disabled = true;

      if (index >= TWELVE_MONTHS - 1) {
        for (var i = 0; i < TWELVE_MONTHS; i++) numerator += array1[index - i].value;

        if(options.average) value = numerator / TWELVE_MONTHS;    //get average value of last 12 months
        else if(options.oneDivider) value = numerator / options.array2[index].value;    //divider is current value of array2
        else {                                                    //divider is also the sum of last 12 months of array2
          for (var i = 0; i < TWELVE_MONTHS; i++) divider += options.array2[index - i].value;
          value = numerator / divider;
        }

        disabled = false;
      }

      return new DateAndValue({
        date,
        value,
        disabled,
      });
    });
  }

  getArrayMultiply(array1, array2, options = { divide: false, yearly: false }) {
    //multiply element values of arrays

    let dateArray = this.ascendantDates;
    if(options.yearly) dateArray = this.yearsFormatted;

    return dateArray.map(
      (date, index) => new DateAndValue({
        date,
        value: options.divide ?
          array1[index].value / array2[index].value :
          array1[index].value * array2[index].value,
        disabled: array1[index].disabled || false,
      })
    );
  }

  getArraySum(array1, array2, options = { yearly: false }) {
    //add element values of arrays

    let dateArray = this.ascendantDates;
    if(options.yearly) dateArray = this.yearsFormatted;

    return dateArray.map(
      (date, index) =>
        ({
          date,
          value: Number(array1[index].value) + Number(array2[index].value),
        })
    );
  }

  getElementByYear(data) {
    //get last month's value of fiscal year

    data = data.reverse();  //data is always date ascendant and we need to calculate from the latest

    return this.yearsFormatted.reverse().map(   //yearsFormatted is also date ascendant
      (date, index) => new DateAndValue({
        date,
        value: data[index * 12].value,      //we need the value of first month of every 12 months in date descendant order
        disabled: false,
      })
    ).reverse();    //we need date ascendant result
  }
}
